<template>
	<div class="token-usage-events-table">
		<ApiTable
			v-model:expanded-rows="expandedRows"
			clickable
			:list-state="listState"
			@row-click="toggleExpand">
			<Column
				expander
				:style="{ width: '20px' }" />
			<Column header="Export">
				<template #body="{ data }">
					<div class="token-usage-events-table__export">
						<div>
							{{ data.build?.app?.name ?? 'Unknown app' }}
						</div>
						<router-link
							class="token-usage-events-table__export-uuid"
							:to="{ name: 'exports-list', query: { s: data.uuid } }"
							>{{ data.uuid }}</router-link
						>
					</div>
				</template>
			</Column>
			<Column
				header="Tokens"
				style="width: 160px">
				<template #body="{ data }">
					<span>{{ totalAmount(data.token_batches ?? []) }} tok</span>
				</template>
			</Column>
			<Column
				header="Usage Date"
				style="width: 220px">
				<template #body="{ data }">
					{{ formatHuman(data.created_at) }}
				</template>
			</Column>

			<template #expansion="slotProps">
				<div class="token-usage-events-table__expansion">
					<div v-if="slotProps.data.token_batches?.length">
						<DataTable
							data-key="id"
							size="small"
							:value="augmentBatches(slotProps.data.token_batches ?? [])">
							<DataTableColumn header="Batch UUID">
								<template #body="{ data }">
									<router-link
										class="token-usage-events-table__batch-uuid"
										:class="{
											'token-usage-events-table__batch-uuid--total':
												'__isTotal' in data && data.__isTotal,
										}"
										:to="{
											name: props.routeName,
											params: props.routeParams,
											query: { tab: 'usage', s: data.uuid },
										}"
										>{{ '__isTotal' in data && data.__isTotal ? 'Total' : data.uuid }}</router-link
									>
								</template>
							</DataTableColumn>
							<DataTableColumn
								header="Tokens"
								:style="{ width: '160px' }">
								<template #body="d">
									<span>{{ (d.data as any).pivot?.amount ?? '?' }} tok</span>
								</template>
							</DataTableColumn>
						</DataTable>
					</div>
					<div
						v-else
						class="token-usage-events-table__empty">
						No token usage recorded.
					</div>
				</div>
			</template>
		</ApiTable>
	</div>
</template>

<script setup lang="ts">
import useApiTable from '@/components/Table/useApiTable'
import useDataTable from '@/components/primevue/useDataTable'
import { watch, ref } from 'vue'
import { useExportListState } from '@/models/Export/States'
import { format } from 'date-fns'
import type { TokenBatch } from '@/models/TokenBatch/Model'

type ChildTokenBatch =
	| TokenBatch
	| { id: '__total__'; uuid: 'Total'; pivot: { amount: number }; __isTotal: true }

const props = withDefaults(
	defineProps<{
		search: string
		routeName?: string
		routeParams?: Record<string, string | number>
		userId?: number
	}>(),
	{
		routeName: 'account-details',
		routeParams: () => ({}),
		userId: undefined,
	},
)

const listState = useExportListState()
listState.defaultParams.with = ['tokenBatches', 'build.app']
listState.defaultParams.orderBy = 'created_at'
listState.defaultParams.order = 'desc'
listState.defaultParams.per_page = 10

const { ApiTable, Column } = useApiTable(listState)
const { DataTable, Column: DataTableColumn } = useDataTable<ChildTokenBatch>()

const expandedRows = ref<any[]>([])

watch(
	() => props.userId,
	(userId) => {
		if (userId) {
			listState.defaultParams.fromRelation = {
				model: 'App\\Models\\User',
				id: userId,
				relation: 'exports',
			}
		} else {
			delete listState.defaultParams.fromRelation
		}
		listState.getList()
	},
	{ immediate: true },
)

function formatHuman(date: string) {
	const d = new Date(date)
	return format(d, 'PPpp')
}

watch(
	() => props.search,
	(val) => {
		const uuid = typeof val === 'string' ? val : undefined
		if (!uuid) {
			listState.query().save()
			listState.getList()
			return
		}
		listState
			.query()
			.whereHas('tokenBatches', (query) => {
				return query.where('uuid', '=', uuid).orWhere('purchase_id', '=', uuid)
			})
			.orWhere('uuid', '=', uuid)
			.save()
		expandedRows.value = []
		listState.getList()
	},
	{ immediate: true },
)

function totalAmount(batches: TokenBatch[]): number {
	if (!Array.isArray(batches)) return 0
	return batches.reduce((sum: number, b: any) => {
		const amt = Number((b?.pivot?.amount as number) ?? 0)
		return sum + (isNaN(amt) ? 0 : amt)
	}, 0)
}

function toggleExpand(event: any) {
	const row = event.data
	const index = expandedRows.value.findIndex((r) => r.id === row.id)
	if (index >= 0) expandedRows.value.splice(index, 1)
	else expandedRows.value.push(row)
}

function augmentBatches(batches: TokenBatch[]): ChildTokenBatch[] {
	if (!Array.isArray(batches)) return []
	const total = totalAmount(batches)
	return [...batches, { id: '__total__', uuid: 'Total', pivot: { amount: total }, __isTotal: true }]
}
</script>

<style scoped lang="scss">
.token-usage-events-table {
	display: flex;
	flex-direction: column;
	gap: 8px;

	.token-usage-events-table__export {
		display: flex;
		flex-direction: column;
		gap: 4px;
		align-items: start;

		.token-usage-events-table__export-uuid {
			font-size: 11px;
			color: var(--text-color);
			opacity: 0.8;
			text-decoration: none;
			&:hover {
				text-decoration: underline;
			}
		}
	}

	.token-usage-events-table__expansion {
		.token-usage-events-table__batch-uuid {
			color: var(--text-color);
			opacity: 0.8;
			text-decoration: none;
			font-family: monospace;
			text-wrap: nowrap;

			&:hover {
				text-decoration: underline;
			}

			&--total {
				font-weight: 700;
				font-family: 'Funnel Sans';

				&:hover {
					text-decoration: none;
				}
			}
		}

		.token-usage-events-table__empty {
			color: var(--text-color-secondary);
			font-size: 12px;
		}
	}
}
</style>
